import React, { MouseEvent, useCallback, useRef } from 'react';
import { useSlateStatic } from 'slate-react';
import { IconButton, Tooltip } from '@ublend-npm/aulaui-next';

import { hasRichTextMark } from '../../../lib/queries';
import { toggleRichTextMark } from '../../../lib/commands';
import { TooltipTitle } from '../../HotkeyLabel/HotkeyLabel.styled';
import { HotkeyLabels } from '../../HotkeyLabel/HotkeyLabel';
import { Hotkey } from '../../../lib/hotkeys';
import { RichTextMark, AulaRange } from '../../../types';

interface RichTextButtonProps {
  label: string;
  icon: JSX.Element;
  hotkeys?: Hotkey[];
  richTextMark: RichTextMark;
  disabled: boolean;
  lastActiveSelection: AulaRange;
}

export const RichTextButton = (props: RichTextButtonProps) => {
  const editor = useSlateStatic();
  const ref = useRef<HTMLButtonElement>(null);

  const { richTextMark, icon, label, hotkeys, disabled, lastActiveSelection } =
    props;

  const selected = hasRichTextMark(editor, richTextMark);

  const toggleFormat = useCallback(
    (
      event:
        | MouseEvent<HTMLButtonElement>
        | (React.KeyboardEvent & { detail: 0 | 1 }),
    ) => {
      event.preventDefault();
      event.stopPropagation();
      toggleRichTextMark(editor, richTextMark, lastActiveSelection);

      // if this is a keyboard event, then focus shifts to the editor
      //  regain focus to the button.
      if (event.detail === 0) {
        setTimeout(() => ref.current?.focus(), 100);
      }
    },
    [editor, richTextMark, lastActiveSelection],
  );

  const tooltip = (
    <TooltipTitle>
      {label}
      {hotkeys && <HotkeyLabels hotkeys={hotkeys} />}
    </TooltipTitle>
  );

  const iconButton = (
    <IconButton
      label={label}
      icon={icon}
      type="secondary"
      size="small"
      disabled={disabled}
      selected={selected}
      onClick={toggleFormat}
      data-testid={`data-testid-${label}`}
      aria-label={label}
      disableRipple={false}
      buttonAttributes={{
        ref,
      }}
      onMouseDown={(e: React.SyntheticEvent) => e.preventDefault()}
    />
  );

  return !disabled ? (
    <Tooltip title={tooltip}>
      <div>{iconButton}</div>
    </Tooltip>
  ) : (
    iconButton
  );
};
